import React, {useCallback, useEffect, useState} from 'react'
import { Box, Select, MenuItem } from '@mui/material'
import PlayerService, { PlayerData } from '@/service/PlayerService'
import GameService from '@/service/GameService'
import { GameItem } from '@/service/GameService'
import {TEAM_NAME} from "@/common/Constant";

interface GameResultProps {
    onClose: () => void,
    onReturn: () => void,
    game: GameItem
}

const SCORE_LIST = Array.from({ length: 11 }, (_, i) => i)

const GameResultTab = ({game, onClose, onReturn}: GameResultProps) => {
    const handleOverlayClick = useCallback(() => onClose(), [onClose])
    const handleReturnClick = useCallback(() => {
        onReturn()}, [onReturn])
    const isSelfGame = game.type === '자체전'
    const teamName = localStorage.getItem('TEAM_NAME') || 'Team A'

    const [players, setPlayers] = useState<PlayerData[]>([])
    const [teamAScore, setTeamAScore] = useState<number | ''>(
        game.teamAScore ?? ''
    )
    const [teamBScore, setTeamBScore] = useState<number | ''>(
        game.teamBScore ?? ''
    )

    useEffect(() => {
        if (!game.id) return
        PlayerService.getPlayersByGame(game.id).then(setPlayers)
    }, [game.id])

    /** ===== 경기 점수 저장 ===== */
    const saveGameScore = async (
        aScore: number | '',
        bScore: number | ''
    ) => {
        if (aScore === '' || bScore === '') return

        await GameService.putGameResult(
            {
                teamAScore: aScore,
                teamBScore: bScore,
                status: '완료'
            },
            game.id!
        )

        handleReturnClick();
    }

    /** ===== 골/어시스트 저장 ===== */
    const updatePlayerStat = async (
        player: PlayerData,
        field: 'goal' | 'assist',
        value: number
    ) => {
        // ✅ UI 먼저 변경
        setPlayers(prev =>
            prev.map(p =>
                p.id === player.id
                    ? { ...p, [field]: value }
                    : p
            )
        )

        // ✅ 서버 저장
        await PlayerService.putPlayerResult({
            ...player,
            [field]: value
        })
    }

    const teamA = players.filter(p => p.teamABType === 'A')
    const teamB = players.filter(p => p.teamABType === 'B')

    return (
        <Box className="game-result">
            {/* ================= SCORE BOARD ================= */}
            <Box className="score-board">
                <Box className="score-team">
                      <span className="score-team-name">
                        {isSelfGame ? 'Team A' : teamName}
                      </span>
                    <Select
                        size="small"
                        className="score-select"
                        value={teamAScore}
                        onChange={e => {
                            const value = Number(e.target.value)
                            setTeamAScore(value)
                            saveGameScore(value, teamBScore)
                        }}
                    >
                        {SCORE_LIST.map(n => (
                            <MenuItem key={n} value={n}>
                                {n}
                            </MenuItem>
                        ))}
                    </Select>
                </Box>

                <span className="score-vs">VS</span>

                <Box className="score-team">
                    <span className="score-team-name">
                      {isSelfGame ? 'Team B' : game.vsName}
                    </span>
                    <Select
                        size="small"
                        className="score-select"
                        value={teamBScore}
                        onChange={e => {
                            const value = Number(e.target.value)
                            setTeamBScore(value)
                            saveGameScore(teamAScore, value)
                        }}
                    >
                        {SCORE_LIST.map(n => (
                            <MenuItem key={n} value={n}>
                                {n}
                            </MenuItem>
                        ))}
                    </Select>
                </Box>
            </Box>

            {/* ================= STATUS ================= */}
            <div
                className={`game-status ${
                    teamAScore !== '' && teamBScore !== '' ? 'completed' : ''
                }`}
            >
                {teamAScore !== '' && teamBScore !== ''
                    ? '경기 종료'
                    : ''}
            </div>

            {/* ================= TEAM A RESULT ================= */}
            <div className={'result-team-wrapper'}>
            <Box className="result-team-box">
                {teamA.length === 0 && (
                    <div className="result-empty"></div>
                )}

                {teamA.map(p => (
                    <div key={p.id} className="result-player">
                        <div className="result-player-info">
                            <span className={`result-player-name ${!p.memberId ? 'mercenary' : ''}`}>{p.name}</span>
                        </div>

                        <div className="result-stats">
                            <Select
                                size="small"
                                className="result-select"
                                value={p.goal ?? 0}
                                onChange={e =>
                                    updatePlayerStat(p, 'goal', Number(e.target.value))
                                }
                            >
                                {SCORE_LIST.map(n => (
                                    <MenuItem key={n} value={n}>
                                        {n}
                                    </MenuItem>
                                ))}
                            </Select>

                            <Select
                                size="small"
                                className="result-select"
                                value={p.assist ?? 0}
                                onChange={e =>
                                    updatePlayerStat(p, 'assist', Number(e.target.value))
                                }
                            >
                                {SCORE_LIST.map(n => (
                                    <MenuItem key={n} value={n}>
                                        {n}
                                    </MenuItem>
                                ))}
                            </Select>
                        </div>
                    </div>
                ))}
            </Box>

            {/* ================= TEAM B RESULT ================= */}
            {isSelfGame && (
                <Box className="result-team-box">
                    {teamB.length === 0 && (
                        <div className="result-empty"></div>
                    )}

                    {teamB.map(p => (
                        <div key={p.id} className="result-player">
                            <div className="result-player-info">
                                <span className={`result-player-name ${!p.memberId ? 'mercenary' : ''}`}>{p.name}</span>
                            </div>

                            <div className="result-stats">
                                <Select
                                    size="small"
                                    className="result-select"
                                    value={p.goal ?? 0}
                                    onChange={e =>
                                        updatePlayerStat(p, 'goal', Number(e.target.value))
                                    }
                                >
                                    {SCORE_LIST.map(n => (
                                        <MenuItem key={n} value={n}>
                                            {n}
                                        </MenuItem>
                                    ))}
                                </Select>

                                <Select
                                    size="small"
                                    className="result-select"
                                    value={p.assist ?? 0}
                                    onChange={e =>
                                        updatePlayerStat(p, 'assist', Number(e.target.value))
                                    }
                                >
                                    {SCORE_LIST.map(n => (
                                        <MenuItem key={n} value={n}>
                                            {n}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </div>
                        </div>
                    ))}
                </Box>
            )}
            </div>
        </Box>

    )
}

export default GameResultTab
